import { useEffect, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { Loader2, ExternalLink } from "lucide-react";

const CANTON_COLOR = { GE: "#378ADD", VD: "#1D9E75", FR: "#A78BFA", NE: "#F4D35E" };

const assetIcon = L.divIcon({
  className: "",
  html: `<div style="width:22px;height:22px;border-radius:6px;background:#EF9F27;border:2px solid #0A0A0F;box-shadow:0 0 8px rgba(239,159,39,.8);display:flex;align-items:center;justify-content:center;font-size:12px">🏠</div>`,
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

export default function SwissMapView({ canton, days, q }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = { days, limit: 1500 };
        if (canton) params.canton = canton;
        if (q) params.q = q;
        const r = await api.get("/workspace/realestate/permits/map-data", { params });
        setData(r.data);
      } catch (e) {
        notify.error(fmtError(e, "Chargement de la carte impossible."));
      } finally { setLoading(false); }
    })();
  }, [canton, days, q]);

  return (
    <div className="space-y-2" data-testid="swiss-map-view">
      <div className="flex items-center gap-4 text-[11px] text-zinc-400 flex-wrap">
        {Object.entries(CANTON_COLOR).map(([c, col]) => (
          <span key={c} className="inline-flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: col }} /> Permis {c}
          </span>
        ))}
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm inline-block bg-[#EF9F27]" /> Vos objets immobiliers
        </span>
        {data && (
          <span className="ml-auto text-zinc-500" data-testid="map-counts">
            {data.permits_count} permis · {data.assets_count} objet(s) géolocalisé(s)
          </span>
        )}
        {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-zinc-500" />}
      </div>

      <div className="rounded-lg overflow-hidden border border-white/10" style={{ height: "560px" }} data-testid="swiss-map-container">
        <MapContainer center={[46.65, 7.2]} zoom={9} style={{ height: "100%", width: "100%", background: "#0A0A0F" }} scrollWheelZoom>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/">CARTO</a>'
            url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
          />
          {(data?.permits || []).map((p) => (
            <CircleMarker
              key={p.dossier_id}
              center={[p.lat, p.lng]}
              radius={5}
              pathOptions={{ color: CANTON_COLOR[p.canton] || "#71717A", fillColor: CANTON_COLOR[p.canton] || "#71717A", fillOpacity: 0.75, weight: 1 }}
            >
              <Popup maxWidth={320}>
                <div style={{ fontSize: 12, lineHeight: 1.5 }}>
                  <div style={{ fontWeight: 700 }}>{p.dossier_id} · {p.commune || p.canton}</div>
                  <div style={{ margin: "4px 0" }}>{(p.description || "").slice(0, 180)}</div>
                  <div style={{ color: "#666" }}>{p.statut} · déposé {p.date_depot}</div>
                  {p.lien_sad && (
                    <a href={p.lien_sad} target="_blank" rel="noreferrer" style={{ color: "#EF9F27", fontWeight: 600 }}>
                      Dossier officiel <ExternalLink style={{ width: 10, height: 10, display: "inline" }} />
                    </a>
                  )}
                </div>
              </Popup>
            </CircleMarker>
          ))}
          {(data?.assets || []).map((a) => (
            <Marker key={a.id} position={[a.lat, a.lng]} icon={assetIcon}>
              <Popup maxWidth={280}>
                <div style={{ fontSize: 12, lineHeight: 1.5 }}>
                  <div style={{ fontWeight: 700 }}>🏠 {a.label}</div>
                  <div>{[a.address, a.commune, a.canton].filter(Boolean).join(", ")}</div>
                  <div style={{ color: "#666" }}>
                    {a.type}{a.valeur_venale_chf ? ` · ${Number(a.valeur_venale_chf).toLocaleString("fr-CH")} CHF` : ""}
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
      <p className="text-[10px] text-zinc-600">
        Vos objets du Portefeuille sont géolocalisés automatiquement (adresse/commune, via swisstopo).
        Les permis sans coordonnées publiées n'apparaissent pas sur la carte.
      </p>
    </div>
  );
}
